import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GitCompare } from 'lucide-react';

interface ProcessStep {
  id: string;
  description: string;
}

interface ProcessComparisonProps {
  currentTitle: string;
  currentDescription: string;
  currentSteps: ProcessStep[];
  futureTitle: string;
  futureDescription: string;
  futureSteps: ProcessStep[];
}

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

interface ProcessContainerProps {
  isFuture: boolean;
}

const ProcessContainer = styled(Box)<ProcessContainerProps>(({ theme, isFuture }) => ({
  padding: theme.spacing(2),
  background: isFuture ? 'rgba(0, 204, 153, 0.05)' : 'rgba(138, 43, 226, 0.05)',
  borderRadius: theme.spacing(1),
  border: `1px solid ${isFuture ? 'rgba(0, 204, 153, 0.2)' : 'rgba(138, 43, 226, 0.2)'}`,
  height: '100%',
}));

const StepsList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const StepItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(1.5),
  '&:last-child': {
    marginBottom: 0,
  },
}));

interface StepNumberProps {
  isFuture: boolean;
}

const StepNumber = styled(Box)<StepNumberProps>(({ theme, isFuture }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1.5),
  background: isFuture ? 'rgba(0, 204, 153, 0.2)' : 'rgba(138, 43, 226, 0.2)',
  color: isFuture ? '#00CC99' : '#8A2BE2',
  fontWeight: 'bold',
  fontSize: '0.75rem',
}));

const ProcessComparison: React.FC<ProcessComparisonProps> = ({
  currentTitle,
  currentDescription,
  currentSteps,
  futureTitle,
  futureDescription,
  futureSteps,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <GitCompare size={20} color="#8A2BE2" style={{ marginRight: 8 }} />
        <Typography variant="h5" sx={{ color: '#fff' }}>
          Process Comparison
        </Typography>
      </Box>

      <SectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ProcessContainer isFuture={false}>
              <Typography variant="h6" sx={{ color: '#8A2BE2', mb: 1 }}>
                Current Process
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2, fontWeight: 'normal' }}>
                {currentTitle}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                {currentDescription}
              </Typography>

              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                Process Steps:
              </Typography>
              <StepsList>
                {currentSteps.map((step, index) => (
                  <StepItem key={step.id}>
                    <StepNumber isFuture={false}>{index + 1}</StepNumber>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {step.description}
                    </Typography>
                  </StepItem>
                ))}
              </StepsList>
            </ProcessContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <ProcessContainer isFuture={true}>
              <Typography variant="h6" sx={{ color: '#00CC99', mb: 1 }}>
                Future Process
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2, fontWeight: 'normal' }}>
                {futureTitle}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                {futureDescription}
              </Typography>

              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                Process Steps:
              </Typography>
              <StepsList>
                {futureSteps.map((step, index) => (
                  <StepItem key={step.id}>
                    <StepNumber isFuture={true}>{index + 1}</StepNumber>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {step.description}
                    </Typography>
                  </StepItem>
                ))}
              </StepsList>
            </ProcessContainer>
          </Grid>
        </Grid>
      </SectionContainer>
    </Box>
  );
};

export default ProcessComparison; 